// The diary's Month view (Liam 30 Sep 2026: his brother keeps his round in Apple Calendar "because he likes he can
// zoom out and see the month"; planned with Astra, docs/plans/month-view/). The pure half: which days the month
// shows, which visits go on a day, the day and week money, and which areas get a colour. The screen is
// src/screens/month.ts.
//
// The rules are the week diary's own (planning.ts), so a visit never shows in one view and not the other:
// - a contact reminder is not a visit (isContact), so it is not on a day;
// - a visit with a week but no day (isUnplaced) is not on a day either: nobody has chosen one;
// - cancelled visits are gone;
// - Roundly's own suggested date (isSuggestion) IS shown, marked as a suggestion, and not counted in the money:
//   hiding it would make a date on the week view vanish here (Astra).

import { currencySymbol } from './round-config'
import { isContact, isSuggestion, isUnplaced } from './planning'
import type { Clean, Customer } from './types'
import { visibleTime } from './business-settings'

export type MonthChipKind = 'agreed' | 'not_agreed' | 'suggested' | 'done_paid' | 'done_unpaid' | 'done'

export interface MonthDay {
  date: string
  inMonth: boolean
  visits: Clean[]
}

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

// "2026-10-14" -> "2026-10-01".
export function monthOf(iso: string): string {
  return `${iso.slice(0, 7)}-01`
}

export function shiftMonth(first: string, n: number): string {
  const [y, m] = first.split('-').map(Number) as [number, number]
  const d = new Date(Date.UTC(y, m - 1 + n, 1, 12))
  return d.toISOString().slice(0, 10)
}

// Monday before (or on) the 1st to the Sunday after (or on) the last day: 28 to 42 days, well inside the
// schedule's 120-day limit.
export function monthRange(first: string): { from: string; to: string } {
  const d = new Date(`${first}T12:00:00Z`)
  const from = addDays(first, -((d.getUTCDay() + 6) % 7))
  const last = shiftMonth(first, 1)
  const lastDay = addDays(last, -1)
  const e = new Date(`${lastDay}T12:00:00Z`)
  const to = addDays(lastDay, (7 - e.getUTCDay()) % 7)
  return { from, to }
}

export function onADay(cl: Clean, from: string, to: string): boolean {
  if (cl.state === 'cancelled') return false
  if (isContact(cl)) return false
  if (isUnplaced(cl, from, to)) return false
  return true
}

export function chipKind(cl: Clean): MonthChipKind {
  if (cl.state === 'done') return cl.payment_status === 'paid' ? 'done_paid' : cl.payment_status === 'unpaid' ? 'done_unpaid' : 'done'
  if (isSuggestion(cl)) return 'suggested'
  return cl.confirmed ? 'agreed' : 'not_agreed'
}

// Only a booked visit moves; done work stays on the day it was done.
export function canMove(cl: Clean): boolean {
  return cl.state === 'booked'
}

export function monthDays(first: string, cleans: readonly Clean[], range = monthRange(first)): MonthDay[] {
  const month = first.slice(0, 7)
  const byDay = new Map<string, Clean[]>()
  for (const cl of cleans) {
    if (cl.date < range.from || cl.date > range.to || !onADay(cl, range.from, range.to)) continue
    const list = byDay.get(cl.date) ?? []
    list.push(cl)
    byDay.set(cl.date, list)
  }
  const out: MonthDay[] = []
  for (let d = range.from; d <= range.to; d = addDays(d, 1)) {
    const visits = (byDay.get(d) ?? []).slice().sort((a, b) => (a.sort_order ?? 1e9) - (b.sort_order ?? 1e9) || (visibleTime(a) ?? '').localeCompare(visibleTime(b) ?? '') || a.id - b.id)
    out.push({ date: d, inMonth: d.slice(0, 7) === month, visits })
  }
  return out
}

// The money a set of visits adds up to, from each visit's own price as the week diary adds it: suggestions are
// not booked work, so they are left out; a visit with no price is counted apart so the total never pretends to
// be complete.
export function dayMoney(visits: readonly Clean[]): { jobs: number; pence: number; noPrice: number } {
  let jobs = 0, pence = 0, noPrice = 0
  for (const cl of visits) {
    if (chipKind(cl) === 'suggested') continue
    jobs++
    const p = cl.amount_pence ?? null
    if (p === null || p === undefined) noPrice++
    else pence += p
  }
  return { jobs, pence, noPrice }
}

// ---- areas -------------------------------------------------------------------------------------------------
// The diary's one area rule (it was schedule.ts's areaOf): the Area typed on the customer, else the first half of
// the postcode ("N10", "WV16"), else "Not set".
export function areaOfClean(cl: Pick<Clean, 'customer_area' | 'customer_postcode'>): string {
  return cl.customer_area?.trim() || cl.customer_postcode?.trim().split(/\s/)[0]?.toUpperCase() || 'Not set'
}
export function areaOfCustomer(c: Pick<Customer, 'area' | 'postcode'>): string {
  return c.area?.trim() || c.postcode?.trim().split(/\s/)[0]?.toUpperCase() || 'Not set'
}

export const AREA_COLOURS = 6

// Which areas get a colour, and which: the round's biggest areas by active customers (two or more each), in that
// order, up to six. Worked out from the whole round, not the month on show, so an area keeps its colour from
// month to month (Astra). The rest, and "Not set", stay grey but can still be picked in the filter.
export function areaColours(customers: readonly Pick<Customer, 'area' | 'postcode' | 'status'>[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const c of customers) {
    if (c.status !== 'active') continue
    const a = areaOfCustomer(c)
    if (a === 'Not set') continue
    counts.set(a, (counts.get(a) ?? 0) + 1)
  }
  const ranked = [...counts.entries()].filter(([, n]) => n >= 2).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  return new Map(ranked.slice(0, AREA_COLOURS).map(([a], i) => [a, i]))
}

// "£25" for whole pounds, "£25.50" otherwise: short enough for a day in a month. The round's money sign.
export function shortMoney(pence: number, sign: string = currencySymbol()): string {
  const pounds = pence / 100
  return `${sign}${Number.isInteger(pounds) ? pounds.toLocaleString('en-GB') : pounds.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// Whose visit it is, by the rule the worker's phone uses (as schedule.ts): the name on it, or the day's roster.
export function visitWorker(cl: Pick<Clean, 'worker' | 'date'>, roster: readonly { date: string; worker?: string | null }[]): string {
  const named = cl.worker?.trim()
  if (named) return named
  return roster.find((r) => r.date === cl.date)?.worker?.trim() ?? ''
}
